import type { HallOfFameGrade } from "@core/certificates/types";
import { cn } from "@design-system/utils";
import { trans, plainTrans } from "@mongez/localization";

type HallOfFameGradeChipsProps = {
  grades: HallOfFameGrade[];
  selected?: number;
  onSelect(grade?: number): void;
};

const chipClassName = (active: boolean) =>
  cn(
    "min-h-10 shrink-0 rounded-full border-2 px-4 text-sm font-black transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40",
    active
      ? "border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100"
      : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
  );

/**
 * Guest grade chips for the hall of fame («كل الصفوف» + every listed grade).
 *
 * @example
 * <HallOfFameGradeChips grades={grades} selected={7} onSelect={setGrade} />
 */
export function HallOfFameGradeChips({
  grades,
  selected,
  onSelect,
}: HallOfFameGradeChipsProps) {
  if (grades.length < 2) return null;

  return (
    <div
      role="group"
      aria-label={plainTrans("certificates.hallOfFame.gradeFilter")}
      className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
    >
      <button
        type="button"
        aria-pressed={!selected}
        onClick={() => onSelect(undefined)}
        className={chipClassName(!selected)}
      >
        {trans("certificates.hallOfFame.allGrades")}
      </button>
      {grades.map((grade) => (
        <button
          key={grade.id}
          type="button"
          aria-pressed={selected === grade.id}
          onClick={() => onSelect(grade.id)}
          className={chipClassName(selected === grade.id)}
        >
          {grade.name}
        </button>
      ))}
    </div>
  );
}
